import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    makeStyles,
    Grid,
} from '@material-ui/core';
import {
    CHART_TYPE,
    CHART_COLUMN_DATA_TYPE,
} from 'utils/enum/BusinessIntelligenceEnum';
import { useQuery } from '@apollo/client';
import ModalUtils from 'utils/ModalUtils';
import NumberUtils from 'lib/NumberUtils';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import Chart from 'components/widgets/businessIntelligence/Chart';
import BIHelper from 'utils/BusinessIntelligenceHelper';

const useStyles = makeStyles({
    grid: {
        paddingLeft: '5px',
        '& > div': {
            width: '100%',
        },
    },
    noData: {
        textAlign: 'center',
        fontSize: '13px',
        marginTop: '45px',
    },
});

const VehiclePricingHistory = ({ stockNumber }) => {
    const classes = useStyles();
    const [state, setState] = useState({
        records: [],
        options: {},
    });

    const {
        records,
        options,
    } = state;

    const {
        data,
        loading,
        error,
    } = useQuery(InventoryQuery.GET_PRICING_HISTORY, {
        variables: { stockNumber },
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }

        if (!loading && data) {
            const response = data?.getPricingHistory;

            if (response) {
                const formattedRecords = response.map((
                    {
                        user,
                        date,
                        price,
                        age,
                    },
                ) => [
                    { name: 'Series', value: 'Price @ Age' },
                    { name: 'Date', value: date },
                    { name: 'Value', value: price },
                    { name: 'Age', value: age },
                    { name: 'User', value: user },
                ]);

                const {
                    configuration: {
                        input,
                        style,
                        filters,
                    },
                } = BIHelper.getChartType().find((el) => el.name === CHART_TYPE.LINE);

                setState((prevState) => ({
                    ...prevState,
                    options: {
                        input: input.map((i) => {
                            const item = { ...i };
                            if (item.name === 'X-Axis') { item.dataType = CHART_COLUMN_DATA_TYPE.DATE; item.value = 'Date'; }
                            if (item.name === 'Series') { item.dataType = CHART_COLUMN_DATA_TYPE.ALPHANUMERIC; item.value = 'Series'; }
                            if (item.name === 'Values') { item.dataType = CHART_COLUMN_DATA_TYPE.NUMERIC; item.value = 'Value'; }

                            return item;
                        }),
                        style: style.map((i) => {
                            const item = { ...i };
                            if (item.name === 'Grid') { item.enabled = true; }
                            if (item.name === 'Stroke') { item.curve = 'straight'; item.lineCap = 'butt'; }
                            if (item.name === 'X-Axis') { item.type = 'datetime'; }
                            if (item.name === 'Y-Axis') { item.title = 'Price'; item.labelsOffsetX = -15; item.titleOffsetX = 10; }
                            if (item.name === 'Markers') { item.size = 5; item.strokeWidth = 0; item.shape = 'circle'; }
                            if (item.name === 'Tooltip') {
                                item.xAxisShow = false;
                                item.yAxisFormatter = (value, chartData) => {
                                    const { dataPointIndex } = chartData;
                                    const age = formattedRecords[dataPointIndex].find((t) => t.name === 'Age')?.value;
                                    const user = formattedRecords[dataPointIndex].find((t) => t.name === 'User')?.value;
                                    return `${NumberUtils.applyThousandsFormat(value, '$0,0.[00]')} @ ${age ?? 0} days changed by ${user}`;
                                };
                            }

                            return item;
                        }),
                        filters,
                    },
                    records: formattedRecords,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, error]);

    return (
        <Grid
            container
            orientation="column"
            className={classes.grid}
        >
            {Object.keys(options).length > 0 && (
                <>
                    {records.length === 0 && (
                        <div className={classes.noData}>
                            No data to display
                        </div>
                    )}
                    {records.length > 0 && (
                        <Chart
                            cache={false}
                            height={250}
                            data={records}
                            type={CHART_TYPE.LINE}
                            options={options}
                            showToolbar={false}
                            showPrintButton={false}
                            hideFilters
                            hideTitle
                            formatAsCurrency
                        />
                    )}
                </>
            )}
        </Grid>
    );
};

VehiclePricingHistory.propTypes = {
    stockNumber: PropTypes.number.isRequired,
};

export default VehiclePricingHistory;
